import { SoundPreset, getSoundDuration } from './presets'

export interface SoundPlayback {
  duration: number
  stop: () => void
}

let audioContext: AudioContext | null = null
const audioElements = new Map<string, HTMLAudioElement>()

const isAudioPreset = (
  preset: SoundPreset,
): preset is Extract<SoundPreset, { audioUrl: string }> => 'audioUrl' in preset

const getAudioElement = (audioUrl: string): HTMLAudioElement => {
  const cachedAudio = audioElements.get(audioUrl)
  if (cachedAudio) {
    return cachedAudio
  }

  const audio = new Audio(audioUrl)
  audio.preload = 'auto'
  audioElements.set(audioUrl, audio)
  return audio
}

export const preloadSoundFiles = (presets: SoundPreset[]): void => {
  presets.filter(isAudioPreset).forEach(preset => {
    const audio = getAudioElement(preset.audioUrl)
    audio.load()
  })
}

const getAudioContext = (): AudioContext | null => {
  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext
  if (!AudioContextConstructor) {
    return null
  }

  audioContext ||= new AudioContextConstructor()
  return audioContext
}

const playAudioFile = async (
  preset: Extract<SoundPreset, { audioUrl: string }>,
  volume: number,
): Promise<SoundPlayback | null> => {
  const audio = getAudioElement(preset.audioUrl)
  audio.pause()
  audio.currentTime = 0
  audio.volume = Math.min(Math.max(volume, 0), 100) / 100

  try {
    await audio.play()
  } catch (error) {
    console.error(`Failed to play notification sound: ${preset.audioUrl}`, error)
    return null
  }

  let stopped = false
  const stop = () => {
    if (stopped) {
      return
    }
    stopped = true
    audio.pause()
    try {
      audio.currentTime = 0
    } catch (_error) {
      // Audio metadata may not be available after an interrupted load.
    }
  }

  return { duration: preset.duration, stop }
}

export const playSoundPreset = async (
  preset: SoundPreset,
  volume: number,
): Promise<SoundPlayback | null> => {
  if (isAudioPreset(preset)) {
    return playAudioFile(preset, volume)
  }

  const context = getAudioContext()
  if (!context) {
    return null
  }

  await context.resume()
  const startsAt = context.currentTime + 0.025
  const output = context.createGain()
  const oscillators: OscillatorNode[] = []
  const normalizedVolume = Math.min(Math.max(volume, 0), 100) / 100
  const duration = getSoundDuration(preset)

  output.gain.setValueAtTime(normalizedVolume, startsAt)
  output.connect(context.destination)

  preset.tones.forEach(tone => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    const toneStartsAt = startsAt + tone.at
    const toneEndsAt = toneStartsAt + tone.duration
    const attackEndsAt = Math.min(toneStartsAt + 0.018, toneEndsAt)

    oscillator.type = tone.wave
    oscillator.frequency.setValueAtTime(tone.frequency, toneStartsAt)
    if (tone.endFrequency) {
      oscillator.frequency.exponentialRampToValueAtTime(tone.endFrequency, toneEndsAt)
    }

    gain.gain.setValueAtTime(0.0001, toneStartsAt)
    gain.gain.exponentialRampToValueAtTime(Math.max(tone.volume, 0.0001), attackEndsAt)
    gain.gain.exponentialRampToValueAtTime(0.0001, toneEndsAt)

    oscillator.connect(gain)
    gain.connect(output)
    oscillator.start(toneStartsAt)
    oscillator.stop(toneEndsAt + 0.02)
    oscillators.push(oscillator)
  })

  let stopped = false
  const disconnectTimer = window.setTimeout(() => output.disconnect(), (duration + 0.12) * 1000)
  const stop = () => {
    if (stopped) {
      return
    }
    stopped = true
    window.clearTimeout(disconnectTimer)
    const now = context.currentTime
    output.gain.cancelScheduledValues(now)
    output.gain.setValueAtTime(Math.max(output.gain.value, 0.0001), now)
    output.gain.exponentialRampToValueAtTime(0.0001, now + 0.035)
    oscillators.forEach(oscillator => {
      try {
        oscillator.stop(now + 0.04)
      } catch (_error) {
        // Oscillator may have already completed naturally.
      }
    })
    window.setTimeout(() => output.disconnect(), 60)
  }

  return { duration, stop }
}
